import Service from 'react-reservice';

import { EAqxTraderThemeMode, createTheme } from '@aquariuxfintech/aqx-trader-sdk-config/theme';

import { profitLossChartViewController } from '@aqx-core/common/services/asset/profit-loss-chart/controller';
import { EThemeTemplate, TENANT_THEMES_MAP } from '@aqx-core/common/services/company/constants';
import { companyViewController } from '@aqx-core/common/services/company/controller';
import { companyService } from '@aqx-core/common/services/company/service';
import { equityChartViewController } from '@aqx-core/common/services/trade/analysis/equity-chart/controller';
import storage, { EStorageKey } from '@aqx-core/common/storage';

import { DARK_THEME_COLORS, HANTEC_DARK_THEME_COLORS, HANTEC_LIGHT_THEME_COLORS, LIGHT_THEME_COLORS } from '../colors';
import { EAppThemeMode, EAppThemeScheme } from '../types';
import { getThemeMode, hexToRgb } from '../utils';
import { appThemeService } from './service';

class AppThemeViewController extends Service {
  private isSkipSetThemeToStorage = false;
  constructor() {
    super();
  }

  init = (themeMode: EAppThemeMode) => {
    const themeScheme: EAppThemeScheme | undefined = storage.getItem(EStorageKey.THEME_SCHEME);
    if (storage.getItem(EStorageKey.THEME_SCHEME) === undefined) {
      this.isSkipSetThemeToStorage = true;
    }
    // Get default theme when no theme scheme is set to the storage
    if (!themeScheme) {
      this.updateTheme(appThemeService.context.themeScheme, appThemeService.context.theme.themeMode);
      return;
    }

    const formattedThemeMode = themeScheme === EAppThemeScheme.SYSTEM_SCHEME ? themeMode : getThemeMode(themeScheme);
    this.updateTheme(themeScheme, formattedThemeMode);
  };

  updateTheme = (nextThemeScheme: EAppThemeScheme, nextThemeMode: EAppThemeMode) => {
    this.setThemeScheme(nextThemeScheme);
    this.setThemeMode(nextThemeMode);
    equityChartViewController.updateChartTheme(nextThemeMode);
    profitLossChartViewController.updateChartTheme();
  };

  setThemeScheme = (nextThemeScheme: EAppThemeScheme) => {
    if (!this.isSkipSetThemeToStorage) {
      storage.setItem(EStorageKey.THEME_SCHEME, nextThemeScheme);
    }
    appThemeService.setThemeScheme(nextThemeScheme);
  };

  setThemeMode = (nextThemeMode: EAppThemeMode) => {
    const { tenantId = '', themeColourCode = 'rgb(0, 188, 216)' } = companyViewController.getTenantIdAndColors();

    const nextThemeScheme =
      nextThemeMode === EAppThemeMode.DARK ? EAppThemeScheme.DARK_SCHEME : EAppThemeScheme.LIGHT_SCHEME;

    this.setThemeByTenant(tenantId, themeColourCode, nextThemeScheme);
  };

  setFontFamily = (nextFontFamily: string) => {
    appThemeService.setFontFamily(nextFontFamily);
  };

  private getTenantAppTheme = (_tenantId: string) => {
    const tenantId = _tenantId || companyService.context.companyProfile?.tenantId;
    const themeTemplate = tenantId ? TENANT_THEMES_MAP[tenantId] : undefined;

    // Here, we will input the custom colors ONLY for the tenant,
    // only what is needed
    if (themeTemplate === EThemeTemplate.HANTEC) {
      return {
        tenantDarkThemeColors: HANTEC_DARK_THEME_COLORS,
        tenantLightThemeColors: HANTEC_LIGHT_THEME_COLORS,
        fontFamily: 'Poppins',
        defaultThemeScheme: EAppThemeScheme.DARK_SCHEME
      };
    }

    // No customisations needed for default
    return {
      tenantDarkThemeColors: DARK_THEME_COLORS,
      tenantLightThemeColors: LIGHT_THEME_COLORS,
      fontFamily: 'IBM Plex Sans',
      defaultThemeScheme: undefined
    };
  };

  setThemeByTenant = (tenantId: string, primaryColor: string, nextThemeScheme?: EAppThemeScheme) => {
    const {
      tenantDarkThemeColors,
      tenantLightThemeColors,
      fontFamily,
      defaultThemeScheme = appThemeService.context.themeScheme
    } = this.getTenantAppTheme(tenantId);
    const themeScheme = nextThemeScheme ?? storage.getItem(EStorageKey.THEME_SCHEME) ?? defaultThemeScheme;

    if (this.isSkipSetThemeToStorage && tenantId) {
      this.isSkipSetThemeToStorage = false;
      this.setThemeScheme(themeScheme);
      storage.setItem(EStorageKey.THEME_SCHEME, themeScheme);
    }
    const themeMode = this.getThemeModeFromThemeScheme(themeScheme);

    if (themeMode === EAppThemeMode.DARK) {
      appThemeService.setThemeModeAndColors({
        themeMode: EAppThemeMode.DARK,
        themeColors: createTheme({
          themeMode: EAqxTraderThemeMode.DARK,
          themeColors: { ...tenantDarkThemeColors, primary: primaryColor }
        }).themeColors
      });
    } else {
      appThemeService.setThemeModeAndColors({
        themeMode: EAppThemeMode.LIGHT,
        themeColors: createTheme({
          themeMode: EAqxTraderThemeMode.LIGHT,
          themeColors: {
            ...tenantLightThemeColors,
            primary: primaryColor
          }
        }).themeColors
      });
    }

    appThemeService.setFontFamily(fontFamily);
  };

  private getThemeModeFromThemeScheme = (themeScheme: EAppThemeScheme): EAppThemeMode => {
    if (themeScheme === EAppThemeScheme.DARK_SCHEME) {
      return EAppThemeMode.DARK;
    }
    if (themeScheme === EAppThemeScheme.LIGHT_SCHEME) {
      return EAppThemeMode.LIGHT;
    }
    const isSystemDarkTheme = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return isSystemDarkTheme ? EAppThemeMode.DARK : EAppThemeMode.LIGHT;
  };

  setPrimaryColor = (nextPrimaryColor: string) => {
    const rgbColor = hexToRgb(nextPrimaryColor);
    if (rgbColor) {
      appThemeService.setPrimaryColor(rgbColor);
    }
  };

  setDefaultCandlestickColors = (positive: string, negative: string) => {
    const candlestick = {
      positive: hexToRgb(positive) || positive,
      negative: hexToRgb(negative) || negative
    };

    appThemeService.setCandlestickColors(candlestick);
  };

  getThemeColors = () => {
    return appThemeService.getThemeColors();
  };
}

export const appThemeViewController = new AppThemeViewController();
